/**
 * The «Группы» wire half's pure logic (ADDITIVE v1.9.5, T.Z50/T.Z51) — the mirror of
 * `apps/api/src/curator/groups-dto.ts`, the same rule every other `api/*-model` file
 * states: the generated `api-types.ts` is the source of truth, this file is the
 * asserted shape the SPA renders, and a divergence is a contract bug.
 *
 * THE OWNER'S RULES the shapes carry (the T.Z50 binding answers):
 *   * a group belongs to a ПЛОЩАДКА (`venueId`), never to a curator;
 *   * the member cap is HARD — `memberLimit` is the api's own constant, so the UI
 *     never re-states 30 from a local literal (a drift here would be a lie about a
 *     rule the server polices);
 *   * the педагог must be of the group's venue — the picker only offers the roster of
 *     the venue the group belongs to, and the api answers a wrong id with 400.
 *
 * A member is `applicationId` + `pguId` + ФИО — the exact shape #26.1 publishes; there
 * is no phone and no e-mail key anywhere in these types (A12/D9).
 */

import { pickList, readPositiveIntOrNull, readStringOrNull } from './curator-model';

/** `#26.8` — one учебная группа of the scope. */
export interface CuratorGroup {
  id: number;
  venueId: number;
  venueName: string;
  name: string;
  teacher: { id: number; fullName: string } | null;
  memberCount: number;
  memberLimit: number;
  members: CuratorGroupMember[];
  createdAt: string;
  updatedAt: string;
}

/** A child on a roster — identifiers and a name, nothing else. */
export interface CuratorGroupMember {
  applicationId: number;
  pguId: string;
  fullName: string;
}

/** `#26.8` — `{data:{groups:[…]}}`. A row that is not shaped as agreed is dropped. */
export function parseCuratorGroups(body: unknown): CuratorGroup[] {
  const groups = pickList(body, 'groups');
  const out: CuratorGroup[] = [];
  for (const candidate of groups) {
    const group = parseGroup(candidate);
    if (group !== null) out.push(group);
  }
  return out;
}

function parseGroup(candidate: unknown): CuratorGroup | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const row = candidate as Record<string, unknown>;
  if (typeof row.name !== 'string') return null;
  const id = readPositiveIntOrNull(row.id);
  const venueId = readPositiveIntOrNull(row.venueId);
  // T.Z70 — a COUNT, not an id: the api maps `memberCount` off `members.length`
  // (`groups-dto.ts`), so a freshly created group legitimately answers 0. Reading it
  // as a positive int dropped every empty group — the create answered 201 and the SPA
  // threw its own «Ответ сервера не содержит группы.», while the list said «Групп
  // пока нет» over the same group. `memberLimit` stays positive: it is the api's own
  // constant (30), never a live count.
  const memberCount = readNonNegativeIntOrNull(row.memberCount);
  const memberLimit = readPositiveIntOrNull(row.memberLimit);
  if (id === null || venueId === null || memberCount === null || memberLimit === null) return null;

  const teacher = parseTeacher(row.teacher);
  const members: CuratorGroupMember[] = [];
  if (Array.isArray(row.members)) {
    for (const entry of row.members) {
      const member = parseMember(entry);
      if (member !== null) members.push(member);
    }
  }
  // The api sends the roster in ФИО order; a re-sort here keeps the table honest even
  // if a cached envelope arrives pre-sorted differently. Cheap: bounded by the cap.
  members.sort((left, right) => left.fullName.localeCompare(right.fullName, 'ru') || left.applicationId - right.applicationId);

  return {
    id,
    venueId,
    venueName: typeof row.venueName === 'string' ? row.venueName : '',
    name: row.name,
    teacher,
    memberCount,
    memberLimit,
    members,
    createdAt: readStringOrNull(row.createdAt) ?? '',
    updatedAt: readStringOrNull(row.updatedAt) ?? '',
  };
}

function parseTeacher(candidate: unknown): { id: number; fullName: string } | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const row = candidate as Record<string, unknown>;
  const id = readPositiveIntOrNull(row.id);
  if (id === null || typeof row.fullName !== 'string') return null;
  return { id, fullName: row.fullName };
}

function parseMember(candidate: unknown): CuratorGroupMember | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const row = candidate as Record<string, unknown>;
  const applicationId = readPositiveIntOrNull(row.applicationId);
  if (applicationId === null || typeof row.fullName !== 'string') return null;
  return {
    applicationId,
    pguId: typeof row.pguId === 'string' ? row.pguId : '',
    fullName: row.fullName,
  };
}

/** `#26.9`/`#26.10`/`#26.12` — `{data:{group}}`. */
export function parseCuratorGroup(body: unknown): CuratorGroup | null {
  if (typeof body !== 'object' || body === null) return null;
  const data = (body as Record<string, unknown>).data;
  if (typeof data !== 'object' || data === null) return null;
  return parseGroup((data as Record<string, unknown>).group);
}

/**
 * The non-negative twin of {@link readPositiveIntOrNull} (T.Z70): a live COUNT on the
 * wire starts at 0 — the head-count of the group the api has just created. Junk still
 * reads as `null`, so the drop-unshaped-rows rule keeps its teeth.
 */
function readNonNegativeIntOrNull(raw: unknown): number | null {
  const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  return Number.isFinite(value) && Number.isInteger(value) && value >= 0 ? value : null;
}

/**
 * The sentence the write guard raises when a single-group answer carries no group —
 * T.Z61's «HTTP 500 (INTERNAL)» the curator saw on a CREATE. It is thrown CLIENT-SIDE
 * (the api had answered 201; no request failed), which is why the wording lives in one
 * constant next to the parser: findable here, pinned by the unit project, and — if it
 * ever appears again — a bug in THIS unwrap, never in the api's logs.
 *
 * It DID appear again (T.Z70, owner 2026-09-16): T.Z61 fixed the double unwrap, but
 * `parseGroup` still read `memberCount` as a POSITIVE int, so the freshly created
 * group (`memberCount: 0`, the api maps it off `members.length`) failed the shape
 * check and the guard fired over the same 201. Two roots, one sentence — both in this
 * file, never in the api's.
 */
export const GROUP_ENVELOPE_MISSING_COPY = 'Ответ сервера не содержит группы.';

/** The write half's outcome: the parsed group, or nothing the render can trust. */
export type CuratorGroupWrite =
  | { ok: true; group: CuratorGroup }
  | { ok: false };

/**
 * #26.9/#26.10/#26.12 — unwrap the RAW response body ONCE, the same `{data:{group}}`
 * the reads model and `groups.routes.spec.ts` pin on the api side.
 *
 * T.Z61 (owner acceptance 2026-09-15): the wire half used to pre-unwrap the envelope
 * with `requestEnvelope(…, ['data'])` and hand the INNER `{group}` object to
 * {@link parseCuratorGroup} — which then looked for `.data` a second time, found
 * nothing, and the SPA reported a 500 over a create the api had answered. The unwrap
 * lives HERE now (the plain-Node `unit` project cannot load `./client`, so it cannot
 * pin the wire half directly), and the wire half passes the raw body through.
 */
export function readGroupWriteEnvelope(body: unknown): CuratorGroupWrite {
  const group = parseCuratorGroup(body);
  return group === null ? { ok: false } : { ok: true, group };
}

// ---------------------------------------------------------------------------
// The optimistic fold — pure, unit-tested, no React (T.Z84)
// ---------------------------------------------------------------------------

/** What #26.13 needs to find its row in a cached list. */
export interface MemberRemoveInput {
  groupId: number;
  applicationId: number;
}

/**
 * T.Z84 — the cache state #26.13 paints BEFORE the server answers: the member is out
 * of the named group's roster and the head-count follows it. The count is recomputed
 * off the roster (not decremented) because that is the api's own mapping —
 * `memberCount` is `members.length` on the wire (T.Z70), so the optimistic state and
 * the state the refetch will bring stay the same shape.
 *
 * Groups that are not the named one come back UNTOUCHED (same object), so a patch
 * over a multi-group cache entry only rewrites the row it means to. An unknown group
 * or an absent member is a no-op, not an error: the server's answer is the truth and
 * `onSettled` resyncs anyway — the optimistic state only has to be plausible for the
 * few hundred milliseconds the request is in flight.
 */
export function withoutGroupMember(
  groups: CuratorGroup[],
  input: MemberRemoveInput,
): CuratorGroup[] {
  return groups.map((group) => {
    if (group.id !== input.groupId) return group;
    const members = group.members.filter((member) => member.applicationId !== input.applicationId);
    if (members.length === group.members.length) return group;
    return { ...group, members, memberCount: members.length };
  });
}

// ---------------------------------------------------------------------------
// Display folds — pure, unit-tested, no React
// ---------------------------------------------------------------------------

/**
 * T.Z61 (owner acceptance 2026-09-15) — the create/edit drawer's «Педагог» picker when
 * the group's venue has NO педагоги (#7.5 `?venueId=` answers an empty page): the
 * field BLOCKS — there is nothing to pick, and the api would 400 the id anyway — and
 * the copy says what to do first. EXACT WORDING, the owner's; change it only with them.
 */
export const NO_TEACHERS_HINT = 'сначала добавьте педагогов для площадки';

/** The picker's state: locked or open, and the hint line under it (`null` = silent). */
export interface TeacherFieldState {
  disabled: boolean;
  hint: string | null;
}

/**
 * The fold the drawer renders. While the roster read is in flight the field stays
 * locked but SILENT — «сначала добавьте педагогов» before the answer arrives would be
 * an instruction the list may be about to disprove. An empty LOADED roster is the
 * only state that speaks (and locks): a create cannot name a teacher that does not
 * exist, and an edit of a group whose педагог the registry no longer lists cannot
 * re-pick one here either.
 */
export function teacherFieldState(input: { pending: boolean; teacherCount: number }): TeacherFieldState {
  if (input.pending) return { disabled: true, hint: null };
  if (input.teacherCount > 0) return { disabled: false, hint: null };
  return { disabled: true, hint: NO_TEACHERS_HINT };
}

/** «12 из 30» — the honest head-count line of a group card. */
export function memberCountLabel(group: Pick<CuratorGroup, 'memberCount' | 'memberLimit'>): string {
  return `${formatCount(group.memberCount)} из ${formatCount(group.memberLimit)}`;
}

/** The teacher line — «Педагог: Иванов И.И.» or the quiet «педагог не назначен». */
export function teacherLabel(group: Pick<CuratorGroup, 'teacher'>): string {
  return group.teacher === null ? 'Педагог не назначен' : `Педагог: ${shortName(group.teacher.fullName)}`;
}

/** «Иванов И.И.» — the roster's compact form; a name it cannot fold stays verbatim. */
export function shortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return fullName.trim();
  const [family, given, ...rest] = parts;
  if (given === undefined || given === '') return fullName.trim();
  const patronymic = rest[0];
  const initials = `${given[0]}.${patronymic !== undefined ? `${patronymic[0]}.` : ''}`;
  return `${family} ${initials}`;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value);
}

/**
 * The «Добавить» list: a venue's offline children MINUS the ones already on the
 * roster. A child without an `applicationId` (no ЕПГУ key — the projection requires
 * one for the roster, so this is defensive) has nothing for #26.12 to name and is
 * left out rather than offered and refused.
 *
 * T.Z78 keeps this signature for the OFFLINE half; {@link groupCandidates} is the
 * fold the block actually renders now, over both sources.
 */
export function candidateMembers(
  children: ReadonlyArray<{ applicationId: number | null; fullName: string | null; pguId: string }>,
  group: Pick<CuratorGroup, 'id' | 'members'>,
): Array<{ applicationId: number; label: string }> {
  return groupCandidates({ offline: children, online: [] }, group).options;
}

/** The two sources of a venue's assignable children, as their reads publish them. */
export interface GroupCandidateSources {
  /**
   * #26.1 — the offline roster; `pguId` is required there, so it reads as `string`.
   * v1.9.32 (T.Z139) — `fullName` may be `null` (the ЕПГУ-only child the ЕВИ letter
   * has not reached): such a child stays OFFERABLE, labelled by its number.
   */
  offline: ReadonlyArray<{
    applicationId: number | null;
    fullName: string | null;
    pguId: string;
    /** ADDITIVE (v1.9.14, T.Z82) — the group the child is in, if any (`null` = none). */
    group?: { id: number; name: string } | null;
  }>;
  /**
   * T.Z78 — the venue's #26.2 rows, MATCHED or not. A MATCHED row carries the
   * `applicationId` #26.12 assigns by; an UNMATCHED one does not, and `pguId`
   * (`row.pguId ?? application.pguId` on the wire) can be `null` even on a matched
   * row whose application the ЕПГУ export never keyed — #26.12 refuses exactly that
   * (400 «has no ЕПГУ number»), so the fold refuses it first.
   */
  online: ReadonlyArray<{
    applicationId: number | null;
    fullName: string;
    pguId: string | null;
    /** ADDITIVE (v1.9.14, T.Z82) — the matched child's group, if any. */
    group?: { id: number; name: string } | null;
  }>;
}

/** What the «Добавить» picker renders, plus what it deliberately does not. */
export interface GroupCandidates {
  /** Assignable children of the venue, both sources, deduped, ФИО order. */
  options: Array<{ applicationId: number; label: string }>;
  /**
   * T.Z78 — ONLINE rows kept out of `options` because #26.12 could not accept them:
   * no matched `applicationId`, or an application without a ЕПГУ number. Counted so
   * the block can say so in one honest line instead of silently shrinking the list.
   */
  withheld: number;
  /**
   * ADDITIVE (v1.9.14, T.Z82) — children kept out because they ALREADY sit in another
   * group of the venue: ONE child = ONE group, and #26.12 answers the second
   * membership with 409 CHILD_ALREADY_IN_GROUP naming that group. A refusal the UI
   * could have predicted is a UI bug, so the list does not offer them — and counts
   * them, so the shrink is said out loud ({@link withheldInGroupHint}). A child of
   * THIS group is a member (`taken`), not a refusal, and counts nothing.
   */
  inOtherGroup: number;
}

/**
 * T.Z78 — the whole «Добавить» list in one fold: the venue's OFFLINE roster AND its
 * ONLINE (#26.2) rows, every entry keyed by the `applicationId` #26.12 takes, the two
 * sources deduped against each other (a prefill row can be matched to an application
 * the offline roster also lists — one child, one option), already-rostered children
 * excluded, and the online rows the api would refuse counted into `withheld`.
 *
 * T.Z82 — children already in ANOTHER group are excluded too (the rows' `group` ref
 * names it), counted into `inOtherGroup`.
 */
export function groupCandidates(
  sources: GroupCandidateSources,
  group: Pick<CuratorGroup, 'id' | 'members'>,
): GroupCandidates {
  const taken = new Set(group.members.map((member) => member.applicationId));
  const seen = new Set<number>();
  const out: Array<{ applicationId: number; label: string }> = [];
  let withheld = 0;
  let inOtherGroup = 0;

  const offer = (
    applicationId: number | null,
    fullName: string | null,
    pguId: string,
    currentGroupId?: number | null,
  ): boolean => {
    if (applicationId === null || pguId === '' || taken.has(applicationId) || seen.has(applicationId)) return false;
    if (currentGroupId !== undefined && currentGroupId !== null && currentGroupId !== group.id) {
      inOtherGroup += 1;
      return false;
    }
    seen.add(applicationId);
    out.push({ applicationId, label: candidateLabel(fullName, pguId) });
    return true;
  };

  for (const child of sources.offline) offer(child.applicationId, child.fullName, child.pguId, child.group?.id ?? null);
  for (const row of sources.online) {
    const pguId = row.pguId ?? '';
    const assignable = row.applicationId !== null && pguId !== '';
    if (!assignable) {
      // The api's own rule (#26.12 → `assertMemberOfVenue`): the member IS an
      // application WITH a ЕПГУ number. No id or no number = #26.12 would answer 400,
      // so the row is not offered — and counted, so the block can say why the list
      // is shorter than the venue's online table. A row whose application is already
      // on the roster (or already offered by the offline half) is not a refusal, it
      // is a member / a duplicate, and counts nothing.
      if (row.applicationId === null || !taken.has(row.applicationId)) withheld += 1;
      continue;
    }
    offer(row.applicationId, row.fullName, pguId, row.group?.id ?? null);
  }
  out.sort((left, right) => left.label.localeCompare(right.label, 'ru'));
  return { options: out, withheld, inOtherGroup };
}

/**
 * T.Z78 — the line under the «Добавить» row when online rows had to stay out. The
 * rule it names is the api's own (#26.12: the member IS an application with a ЕПГУ
 * number), and the way out is named — the matcher, not a dead end.
 */
export function withheldOnlineHint(withheld: number): string | null {
  if (withheld <= 0) return null;
  return `${withheld} ${pluralRu(withheld, ['онлайн-строка', 'онлайн-строки', 'онлайн-строк'])} пока не предлагается: нет сопоставленного заявления с номером ЕПГУ. Строки сопоставятся автоматически — тогда их можно добавить.`;
}

/**
 * T.Z82 — the line under the «Добавить» row when children had to stay out because
 * they ALREADY sit in another group (ONE child = ONE group). The rule it names is the
 * api's own (#26.12 answers the second membership with 409 CHILD_ALREADY_IN_GROUP),
 * and the way out is named — remove the child from its group first, which is exactly
 * what a row drawer's move does.
 */
export function withheldInGroupHint(inOtherGroup: number): string | null {
  if (inOtherGroup <= 0) return null;
  return `${inOtherGroup} ${pluralRu(inOtherGroup, ['ученик', 'ученика', 'учеников'])} уже в другой группе — один ребёнок состоит только в одной группе.`;
}

/** RU plural, the venues-model rule: `1 / 2 / 5`, the 11–14 teens genitive plural. */
function pluralRu(count: number, forms: readonly [string, string, string]): string {
  const n = Math.max(Math.round(count), 0);
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/**
 * «Фамилия И.О. · 7000000001» — a name plus the identifier the api keys by.
 * v1.9.32 (T.Z139) — a child with no ФИО yet reads «Без ФИО · 7000000001»: still
 * assignable (#26.12 takes the applicationId, not a name), just named by its number.
 */
export function candidateLabel(fullName: string | null, pguId: string): string {
  const name = fullName === null ? 'Без ФИО' : shortName(fullName);
  return pguId === '' ? name : `${name} · ${pguId}`;
}

/** The group card's fullness tone: `ok` under the cap, `warn` from 80% of it, `bad` at it. */
export function fullnessTone(group: Pick<CuratorGroup, 'memberCount' | 'memberLimit'>): 'ok' | 'warn' | 'bad' {
  if (group.memberCount >= group.memberLimit) return 'bad';
  // A RATIO, not «within 5»: the api owns the limit, and a small group would read
  // «warn» from its first member under an absolute rule.
  if (group.memberCount * 5 >= group.memberLimit * 4) return 'warn';
  return 'ok';
}

/**
 * Whether the DELETE control may be offered: only an EMPTY group is deletable
 * (409 GROUP_NOT_EMPTY otherwise), and the copy says so before the click — the
 * repo's rule that a refusal the UI could have predicted is a UI bug.
 */
export function canDeleteGroup(group: Pick<CuratorGroup, 'memberCount'>): boolean {
  return group.memberCount === 0;
}
